import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import {
  BodyText,
  Button,
  enforceNoFractionalLigatures,
  formatDate,
  Modal,
  theme,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import type { ChallengeCelebration } from '../../graphql/types';
import { useChallengesTelemetry } from '../../screens/ChallengesScreen/ChallengesScreen.telemetry';

// ─────────────────────────────────────────────────────────────────────────

export const ChallengeCelebrationModal = (
  props: ChallengeCelebrationModalProps,
) => {
  const { celebration, onSelectRewards } = props;
  const [visible, setVisible] = useState(true);

  const {
    handleMyRewardsTelemetry,
    handleOpenChallengeCelebrationTelemetry,
    handleCloseChallengeCelebrationTelemetry,
  } = useChallengesTelemetry();
  const { t } = useLocalizationContext();

  const expiration = celebration.expirationDate
    ? new Date(celebration.expirationDate)
    : null;

  const formattedDate = formatDate(expiration);

  const onRequestClose = useCallback(() => {
    setVisible(false);
  }, []);

  const onNavigateToRewards = useCallback(() => {
    onRequestClose();
    onSelectRewards?.();
    handleMyRewardsTelemetry();
  }, [onRequestClose, onSelectRewards, handleMyRewardsTelemetry]);

  useEffect(() => {
    if (visible) handleOpenChallengeCelebrationTelemetry(celebration);
    else handleCloseChallengeCelebrationTelemetry();
  }, [
    visible,
    handleOpenChallengeCelebrationTelemetry,
    handleCloseChallengeCelebrationTelemetry,
    celebration,
  ]);

  return (
    <Modal
      size="small"
      animationType="fade"
      palette="secondary"
      visible={visible}
      onRequestClose={onRequestClose}
    >
      <Modal.CloseBtn palette="secondary" onPress={onRequestClose} />
      <Modal.Headline
        palette="secondary"
        style={styles.heading}
        testID="challenge.celebration.modal.headline"
      >
        {enforceNoFractionalLigatures(celebration.celebrationCopy)}
      </Modal.Headline>
      <Modal.Subhead palette="secondary">
        {enforceNoFractionalLigatures(celebration.title)}
      </Modal.Subhead>
      {formattedDate ? (
        <Modal.Row style={[styles.expirationRow, styles.row]}>
          <BodyText size={3} style={styles.textWhite}>
            {t('general.ends-on-date', { date: formattedDate })}.
          </BodyText>
        </Modal.Row>
      ) : null}
      <Modal.Row style={styles.row}>
        <BodyText size={3} style={styles.textWhite}>
          {celebration.rewardUsageDisclaimer}
        </BodyText>
      </Modal.Row>
      <Modal.Footer withoutTopBorder style={styles.footer}>
        <Button
          palette="outlinedLight"
          onPress={onNavigateToRewards}
          testID="challenge.celebration.modal.cta"
        >
          {t('challenges.rewards.see-my-rewards')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type ChallengeCelebrationModalProps = {
  readonly celebration: ChallengeCelebration;
  readonly onSelectRewards?: () => void;
};

// ─── STYLES ──────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  heading: {
    paddingBottom: theme.spacing.small,
  },
  footer: {
    paddingTop: theme.spacing.large,
  },
  expirationRow: {
    paddingBottom: theme.spacing.large,
  },
  row: {
    paddingTop: theme.spacing.medium,
  },
  textWhite: {
    color: theme.colors.WHITE,
  },
});
